import React, { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@mui/material';
import { DollarSign, Package } from 'lucide-react';
import { EmployeeSalesReportItem } from 'src/services/apiService/response-models/report';

interface EmployeeSalesReportProps {
  data: EmployeeSalesReportItem[];
}

// Reusable summary card
const SummaryCard = ({ title, value, icon, description }: { title: string; value: React.ReactNode; icon?: React.ReactNode; description: string }) => (
  <Card>
    <CardHeader
      title={<span className="text-sm font-medium">{title}</span>}
      action={icon}
      style={{ paddingBottom: 0 }}
    />
    <CardContent>
      <div className="text-2xl font-bold" style={{ fontSize: 28, fontWeight: 700 }}>
        {value}
      </div>
      <p className="text-xs text-muted-foreground" style={{ fontSize: 12, color: '#888' }}>
        {description}
      </p>
    </CardContent>
  </Card>
);

const columns = [
  { key: 'employeeName', label: 'Employee Name', align: 'left', isCurrency: false },
  { key: 'totalSales', label: 'Total Sales', align: 'right', isCurrency: true },
  { key: 'totalTips', label: 'Total Tips', align: 'right', isCurrency: true },
  { key: 'totalDiscounts', label: 'Total Discounts', align: 'right', isCurrency: true },
  { key: 'orderCount', label: 'Order Count', align: 'right', isCurrency: false },
  { key: 'avgCheckSize', label: 'Avg Check Size', align: 'right', isCurrency: true },
  { key: 'totalVoids', label: 'Total Voids', align: 'right', isCurrency: false }
] as const;

type ColumnKey = typeof columns[number]['key'];

type SortConfig = { key: ColumnKey; direction: 'asc' | 'desc' };

const EmployeeSalesReportSummary: React.FC<EmployeeSalesReportProps> = ({ data }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'employeeName', direction: 'asc' });

  const sortedData = useMemo(() => {
    if (!data) return [];
    const sorted = [...data];
    sorted.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const handleSort = (key: ColumnKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const summary = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalSales: 0,
        totalTips: 0,
        totalDiscounts: 0,
        totalOrders: 0,
        totalVoids: 0
      };
    }
    return {
      totalSales: data.reduce((sum, e) => sum + e.totalSales, 0),
      totalTips: data.reduce((sum, e) => sum + e.totalTips, 0),
      totalDiscounts: data.reduce((sum, e) => sum + e.totalDiscounts, 0),
      totalOrders: data.reduce((sum, e) => sum + e.orderCount, 0),
      totalVoids: data.reduce((sum, e) => sum + e.totalVoids, 0)
    };
  }, [data]);

  return (
    <div style={{ padding: 24 }}>
      <h2>Employee Sales Report</h2>
      {/* Summary Cards */}
      <div
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 32
        }}
      >
        <SummaryCard
          title="Total Sales"
          value={`$${summary.totalSales.toFixed(2)}`}
          icon={<DollarSign size={20} style={{ color: '#888' }} />}
          description="Across all employees"
        />
        <SummaryCard
          title="Total Orders"
          value={summary.totalOrders}
          icon={<Package size={20} style={{ color: '#888' }} />}
          description="Orders handled by all employees"
        />
        <SummaryCard
          title="Total Tips"
          value={`$${summary.totalTips.toFixed(2)}`}
          description="Tips received by all employees"
        />
        <SummaryCard
          title="Total Discounts"
          value={`$${summary.totalDiscounts.toFixed(2)}`}
          description="Discounts given by all employees"
        />
        <SummaryCard
          title="Total Voids"
          value={summary.totalVoids}
          description="Total voids across all employees"
        />
      </div>
      {/* Employee Table */}
      <Card>
        <CardHeader title={<span>Employee Sales Table</span>} />
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align as any}
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    {col.label} {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((emp) => (
                <TableRow key={emp.employeeName + emp.totalSales}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align as any}>
                      {col.isCurrency === true ? `$${(emp[col.key] as number).toFixed(2)}` : emp[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeSalesReportSummary;
